import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'src/store';

export interface ProjectAccess {
  isBillingVisible: boolean;
  isStrict: boolean;
  canSeeCredits: boolean;
  canBuyHours: boolean;
  canSeeRequestTime: boolean;
  canSeePlan: boolean;
  canSeeReports: boolean;
  canEditFollowers: boolean;
  canInviteMembers: boolean;
}

export type ProjectAccessPermission = Exclude<
  keyof ProjectAccess,
  'isBillingVisible' | 'isStrict'
>;

// Projects with client_billing_visible off hide all billing for everyone on the project.
// Strict-request contacts hide credits, reports, follower edits and invites for that contact only.
export const getProjectAccess = (
  project: Record<string, any> | null | undefined
): ProjectAccess => {
  const isBillingVisible = project?.client_billing_visible !== false;
  const isStrict = !!project?.customer?.strict_requests;
  const canSeeCredits = !!project && isBillingVisible && !isStrict;

  return {
    isBillingVisible,
    isStrict,
    canSeeCredits,
    canBuyHours: canSeeCredits,
    canSeeRequestTime: isBillingVisible,
    canSeePlan: isBillingVisible,
    canSeeReports: !isStrict,
    canEditFollowers: !isStrict,
    canInviteMembers: !isStrict,
  };
};

const useProjectAccess = (): ProjectAccess => {
  const project = useSelector(
    (globalState: RootState) => globalState.projectRetainer.data
  );

  return useMemo(() => getProjectAccess(project), [project]);
};

export default useProjectAccess;
